// Public intake form page (/f/:slug) — NO login required. Renders a portal-
// enabled form for an anonymous end-user, applies branching live, validates the
// visible required fields, and submits to the public endpoint. Lives OUTSIDE the
// authed Layout/RequireAuth in App.tsx.

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { ApiError, getFormBySlug, submitForm } from "../lib/api";
import { Alert, Loading, Spinner } from "../components/States";
import { Logo } from "../components/Logo";
import type { FormBranchingRule, FormDetailRow, FormFieldRow } from "../lib/types";

function inputType(kind: string): string {
  if (kind === "date") return "date";
  if (kind === "number") return "number";
  if (kind === "email") return "email";
  return "text";
}

export function PublicFormPage() {
  const { slug = "" } = useParams();
  const [form, setForm] = useState<FormDetailRow | null>(null);
  const [fields, setFields] = useState<FormFieldRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | boolean>>({});
  const setAnswer = (key: string, value: string | boolean) => setAnswers((a) => ({ ...a, [key]: value }));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Load the form anonymously (portal-enabled forms are public; others 404).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getFormBySlug(slug)
      .then((res) => {
        if (cancelled) return;
        setForm(res.form);
        setFields(res.fields ?? []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof ApiError && e.status === 404
            ? "This form isn't available. It may be unpublished or the link is incorrect."
            : e instanceof Error
              ? e.message
              : "Could not load this form.",
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  /** Branching: a field shows when it has no rule or its showIf condition holds
   *  (loose string compare, mirroring the server's visibleFields). */
  function isVisible(f: FormFieldRow): boolean {
    if (!f.branching_json) return true;
    try {
      const rule = JSON.parse(f.branching_json) as FormBranchingRule;
      if (!rule?.showIf?.field) return true;
      return String(answers[rule.showIf.field] ?? "") === String(rule.showIf.equals);
    } catch {
      return true;
    }
  }
  const visible = useMemo(() => fields.filter(isVisible), [fields, answers]); // eslint-disable-line react-hooks/exhaustive-deps

  // A visible required field is unmet when blank / false.
  const missing = visible.filter((f) => {
    if (!f.required) return false;
    const v = answers[f.key];
    return f.kind === "checkbox" ? !v : !String(v ?? "").trim();
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (missing.length || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Send only the answers for currently-visible fields.
      const payload: Record<string, unknown> = {};
      for (const f of visible) if (answers[f.key] !== undefined) payload[f.key] = answers[f.key];
      await submitForm(slug, payload);
      setDone(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="public-form-shell">
      <div className="public-form-card">
        <div className="public-form-brand">
          {form?.logo_url ? (
            <img src={form.logo_url} alt={form.name} className="public-form-logo" />
          ) : (
            <Logo size={26} />
          )}
        </div>

        {loading ? (
          <Loading label="Loading form…" />
        ) : loadError ? (
          <Alert kind="error">{loadError}</Alert>
        ) : done ? (
          <div className="public-form-done">
            <div className="public-form-check" aria-hidden="true">✓</div>
            <h1>Thank you!</h1>
            <p className="muted">Your response to “{form?.name}” has been submitted.</p>
          </div>
        ) : form ? (
          <>
            <h1 className="public-form-title">{form.name}</h1>
            {form.description ? <p className="public-form-desc">{form.description}</p> : null}
            <form onSubmit={onSubmit} className="form-preview">
              {submitError ? <Alert kind="error">{submitError}</Alert> : null}
              {visible.map((f) => (
                <label className="field" key={f.id}>
                  <span className="label">
                    {f.label}
                    {f.required ? <span style={{ color: "var(--danger, #dc2626)" }}> *</span> : null}
                  </span>
                  {f.kind === "textarea" ? (
                    <textarea
                      rows={4}
                      value={String(answers[f.key] ?? "")}
                      onChange={(e) => setAnswer(f.key, e.target.value)}
                    />
                  ) : f.kind === "select" ? (
                    <select value={String(answers[f.key] ?? "")} onChange={(e) => setAnswer(f.key, e.target.value)}>
                      <option value="">— select —</option>
                      {parseOptions(f.options_json).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.kind === "checkbox" ? (
                    <span className="toggle">
                      <input
                        type="checkbox"
                        checked={!!answers[f.key]}
                        onChange={(e) => setAnswer(f.key, e.target.checked)}
                      />{" "}
                      {f.label}
                    </span>
                  ) : (
                    <input
                      type={inputType(f.kind)}
                      value={String(answers[f.key] ?? "")}
                      onChange={(e) => setAnswer(f.key, e.target.value)}
                    />
                  )}
                </label>
              ))}
              <button className="btn primary" type="submit" disabled={submitting || missing.length > 0}>
                {submitting ? <Spinner label="Submitting…" /> : "Submit"}
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}

function parseOptions(json: string | null): string[] {
  try {
    return json ? (JSON.parse(json) as string[]) : [];
  } catch {
    return [];
  }
}
